import React, { useState } from "react";
import api from "@/lib/apiClient";
import { localizedPath, useLang } from "@/lib/i18n";
import useSEO from "@/lib/useSEO";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { MessageSquarePlus, Send, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";

const CATEGORIES = [
  { value: "request_instrument", label_id: "Usulan tes baru", label_en: "Suggest a new test" },
  { value: "bug", label_id: "Laporan bug", label_en: "Report a bug" },
  { value: "technical", label_id: "Kendala teknis", label_en: "Technical issue" },
  { value: "suggestion", label_id: "Saran umum", label_en: "General suggestion" },
  { value: "other", label_id: "Lainnya", label_en: "Other" },
];

export default function Feedback() {
  const { lang } = useLang();
  useSEO({
    title: lang === "id" ? "Umpan Balik — Mindovia" : "Feedback — Mindovia",
    description: lang === "id"
      ? "Usulkan tes baru, laporkan kendala, atau bagikan ide ke tim Mindovia. Kami membaca setiap pesan."
      : "Suggest a new test, report a problem, or share an idea with the Mindovia team. We read every message.",
    url: "/feedback",
    image: "/og-cover.png",
  });
  const [form, setForm] = useState({ name: "", email: "", category: "suggestion", message: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const upd = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email || form.message.trim().length < 5) {
      toast.error(lang === "id" ? "Email dan pesan wajib diisi (minimal 5 karakter)." : "Please enter your email and a message (at least 5 characters).");
      return;
    }
    setBusy(true);
    try {
      await api.post("/public/feedback", {
        ...form,
        context_url: (typeof window !== "undefined" ? window.location.href : ""),
      });
      setSent(true);
      toast.success(lang === "id" ? "Terima kasih! Pesanmu sudah kami terima." : "Thanks! We've received your message.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-2xl mx-auto px-6 py-12" data-wrap="responsive">
      <div className="inline-flex items-center gap-2 text-xs uppercase tracking-wider text-terracotta-700 mb-3">
        <MessageSquarePlus className="w-4 h-4" />
        {lang === "id" ? "Pusat Umpan Balik" : "Feedback Center"}
      </div>
      <h1 className="font-display text-4xl md:text-5xl tracking-tight leading-tight">
        {lang === "id" ? "Bantu Mindovia jadi lebih baik." : "Help us make Mindovia better."}
      </h1>
      <p className="text-muted-foreground mt-3 max-w-xl">
        {lang === "id"
          ? "Usulkan tes baru, laporkan kendala, atau bagikan ide. Kami membaca setiap pesan."
          : "Suggest a new test, report a problem, or share an idea. We read every message."}
      </p>

      {sent ? (
        <div className="mt-10 p-6 rounded-2xl bg-sage-50 border border-sage-200" data-testid="feedback-thanks">
          <div className="font-display text-xl text-teal-800 mb-2">{lang === "id" ? "Pesan terkirim" : "Message received"}</div>
          <p className="text-sm text-teal-800/80">
            {lang === "id" ? "Kami akan menindaklanjutinya sesuai kategori yang kamu pilih. Sambil menunggu, coba tes lainnya, yuk." : "We'll follow up based on the category you chose. In the meantime, feel free to try another test."}
          </p>
          <div className="mt-4 flex gap-2">
            <Button onClick={() => { setSent(false); setForm({ name: "", email: "", category: "suggestion", message: "" }); }} variant="outline" className="rounded-full" data-testid="feedback-another">
              {lang === "id" ? "Kirim yang lain" : "Send another"}
            </Button>
            <Link to={localizedPath("/instruments", lang)}><Button variant="ghost" className="rounded-full"><ArrowLeft className="w-4 h-4 mr-1" /> {lang === "id" ? "Lihat tes" : "Browse tests"}</Button></Link>
          </div>
        </div>
      ) : (
        <form onSubmit={submit} className="mt-8 space-y-4">
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">{lang === "id" ? "Nama (opsional)" : "Name (optional)"}</Label>
              <Input value={form.name} onChange={(e) => upd("name", e.target.value)} data-testid="feedback-name" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Email *</Label>
              <Input type="email" required value={form.email} onChange={(e) => upd("email", e.target.value)} data-testid="feedback-email" />
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">{lang === "id" ? "Kategori" : "Category"}</Label>
            <Select value={form.category} onValueChange={(v) => upd("category", v)}>
              <SelectTrigger data-testid="feedback-category"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{lang === "id" ? c.label_id : c.label_en}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">{lang === "id" ? "Pesan" : "Message"} *</Label>
            <Textarea rows={6} required value={form.message} onChange={(e) => upd("message", e.target.value)} data-testid="feedback-message" />
          </div>
          <div>
            <Button type="submit" disabled={busy} className="rounded-full bg-terracotta-500 hover:bg-terracotta-600 text-cream-50" data-testid="feedback-submit-btn">
              <Send className="w-4 h-4 mr-2" /> {busy ? (lang === "id" ? "Mengirim…" : "Sending…") : (lang === "id" ? "Kirim Umpan Balik" : "Send Feedback")}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}
